import React, { useMemo, useState, createElement, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { InfoIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, ArrowUpDownIcon, ArrowRightIcon, ChevronRightIcon, InfoIcon as InfoCircleIcon, AlertTriangleIcon, XIcon, ShieldIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { daysFromNow, randomSecondsAgo } from '../../../shared/lib/relativeDates';
import { getChartDataForCompany, getTotalForCompany, getPayoutScheduleForCompany } from '../mocks/balanceData';
const formatShortDate = (d: Date) => d.toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'short'
});
const formatFullDate = (d: Date) => d.toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
});
export function BalanceSummary() {
  const {
    t
  } = useTranslation('balance');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  // Generate dynamic dates
  const dates = useMemo(() => {
    return {
      d0: daysFromNow(0),
      d1: daysFromNow(1),
      d2: daysFromNow(2),
      d3: daysFromNow(3),
      d4: daysFromNow(4),
      d5: daysFromNow(5),
      d6: daysFromNow(6),
      d7: daysFromNow(7),
      d10: daysFromNow(10),
      d17: daysFromNow(17),
      d24: daysFromNow(24),
      d31: daysFromNow(31),
      d38: daysFromNow(38),
      d45: daysFromNow(45)
    };
  }, []);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedDates, setExpandedDates] = useState<Record<string, string>>({
    incoming: formatFullDate(dates.d0),
    available: formatFullDate(dates.d0),
    reserved: formatFullDate(dates.d10)
  });
  const [simulateNegative, setSimulateNegative] = useState(false);
  const [showMinBanner, setShowMinBanner] = useState(true);
  const [showMinBalanceModal, setShowMinBalanceModal] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [minBalanceSaved, setMinBalanceSaved] = useState(false);
  // Data for Incoming section
  const incomingChartData = [{
    date: formatShortDate(dates.d0),
    amount: 1000
  }, {
    date: formatShortDate(dates.d1),
    amount: 800
  }, {
    date: formatShortDate(dates.d3),
    amount: 620
  }, {
    date: formatShortDate(dates.d4),
    amount: 380
  }, {
    date: formatShortDate(dates.d5),
    amount: 200
  }];
  const incomingTransactions = [{
    id: 'ch_4Ax1a1',
    customer: 'Malee Fashion',
    amount: 720
  }, {
    id: 'ch_4Ax1a2',
    customer: 'Chai Electronics',
    amount: 480
  }];
  // Data for Available section
  const availableChartData = [{
    date: formatShortDate(dates.d0),
    amount: 45230
  }, {
    date: formatShortDate(dates.d1),
    amount: 3500
  }, {
    date: formatShortDate(dates.d4),
    amount: 2100
  }, {
    date: formatShortDate(dates.d5),
    amount: 800
  }, {
    date: formatShortDate(dates.d6),
    amount: 600
  }, {
    date: formatShortDate(dates.d7),
    amount: 200
  }];
  const availableTransactions = [{
    id: 'ch_3Qx1a2',
    customer: 'Somchai Wattanachai',
    amount: 18500
  }, {
    id: 'ch_3Qx1b7',
    customer: 'Siriporn Trading',
    amount: 12450.8
  }, {
    id: 'ch_3Qx1c4',
    customer: 'Nattapong Wongsakul',
    amount: 8750
  }, {
    id: 'ch_3Qx1d9',
    customer: 'Somchai Enterprises Co., Ltd.',
    amount: 5530
  }];
  // Data for Reserved section
  const reservedChartData = [{
    date: formatShortDate(dates.d10),
    amount: 1850
  }, {
    date: formatShortDate(dates.d17),
    amount: 2100
  }, {
    date: formatShortDate(dates.d24),
    amount: 1750
  }, {
    date: formatShortDate(dates.d31),
    amount: 2400
  }, {
    date: formatShortDate(dates.d38),
    amount: 1980
  }, {
    date: formatShortDate(dates.d45),
    amount: 2370
  }];
  const reservedTransactions = [{
    id: 'rsv_1a',
    customer: 'Somchai Wattanachai',
    amount: 925
  }, {
    id: 'rsv_1b',
    customer: 'Siriporn Trading',
    amount: 625
  }, {
    id: 'rsv_1c',
    customer: 'Nattapong Wongsakul',
    amount: 300
  }];
  // Data for On hold section
  const onHoldChartData = [{
    date: formatShortDate(dates.d5),
    amount: 1500
  }, {
    date: formatShortDate(dates.d10),
    amount: 2000
  }, {
    date: formatShortDate(dates.d15 || daysFromNow(15)),
    amount: 2000
  }];
  const onHoldTransactions = [{
    id: 'hld_1a',
    customer: 'Review: High risk txn',
    amount: 2500
  }, {
    id: 'hld_1b',
    customer: 'Review: Unusual volume',
    amount: 3000
  }];
  // Mock data with companyId assignments
  const allBalanceData = useMemo(() => ({
    incoming: {
      'master-001': {
        amount: 3250.0,
        chartData: incomingChartData,
        transactions: incomingTransactions
      },
      'submerchant-kl-001': {
        amount: 850.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 850
        }],
        transactions: [{
          id: 'ch_4Ax1a1',
          customer: 'Majeedhee Magu Customer',
          amount: 850
        }]
      },
      'submerchant-kl-002': {
        amount: 620.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 620
        }],
        transactions: [{
          id: 'ch_4Ax1a2',
          customer: 'BB Customer',
          amount: 620
        }]
      },
      'submerchant-kl-003': {
        amount: 480.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 480
        }],
        transactions: [{
          id: 'ch_4Ax1a3',
          customer: 'MK Customer',
          amount: 480
        }]
      },
      'submerchant-kl-004': {
        amount: 550.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 550
        }],
        transactions: [{
          id: 'ch_4Ax1a4',
          customer: 'Galolhu Customer',
          amount: 550
        }]
      },
      'submerchant-kl-005': {
        amount: 400.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 400
        }],
        transactions: [{
          id: 'ch_4Ax1a5',
          customer: 'MV Customer',
          amount: 400
        }]
      },
      'submerchant-kl-006': {
        amount: 350.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 350
        }],
        transactions: [{
          id: 'ch_4Ax1a6',
          customer: 'Addu City Customer',
          amount: 350
        }]
      }
    },
    available: {
      'master-001': {
        amount: 61767.25,
        chartData: availableChartData,
        transactions: availableTransactions
      },
      'submerchant-kl-001': {
        amount: 12500.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 12500
        }],
        transactions: [{
          id: 'ch_3Qx1a2',
          customer: 'Majeedhee Magu Payment',
          amount: 12500
        }]
      },
      'submerchant-kl-002': {
        amount: 10800.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 10800
        }],
        transactions: [{
          id: 'ch_3Qx1b7',
          customer: 'BB Payment',
          amount: 10800
        }]
      },
      'submerchant-kl-003': {
        amount: 9200.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 9200
        }],
        transactions: [{
          id: 'ch_3Qx1c4',
          customer: 'MK Payment',
          amount: 9200
        }]
      },
      'submerchant-kl-004': {
        amount: 11400.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 11400
        }],
        transactions: [{
          id: 'ch_3Qx1d9',
          customer: 'Galolhu Payment',
          amount: 11400
        }]
      },
      'submerchant-kl-005': {
        amount: 8900.0,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 8900
        }],
        transactions: [{
          id: 'ch_3Qx1e1',
          customer: 'MV Payment',
          amount: 8900
        }]
      },
      'submerchant-kl-006': {
        amount: 8967.25,
        chartData: [{
          date: formatShortDate(dates.d0),
          amount: 8967.25
        }],
        transactions: [{
          id: 'ch_3Qx1f2',
          customer: 'Addu City Payment',
          amount: 8967.25
        }]
      }
    },
    reserved: {
      'master-001': {
        amount: 12450.0,
        chartData: reservedChartData,
        transactions: reservedTransactions
      },
      'submerchant-kl-001': {
        amount: 2100.0,
        chartData: [{
          date: formatShortDate(dates.d10),
          amount: 2100
        }],
        transactions: [{
          id: 'rsv_1a',
          customer: 'Majeedhee Magu Reserve',
          amount: 2100
        }]
      },
      'submerchant-kl-002': {
        amount: 1850.0,
        chartData: [{
          date: formatShortDate(dates.d10),
          amount: 1850
        }],
        transactions: [{
          id: 'rsv_1b',
          customer: 'BB Reserve',
          amount: 1850
        }]
      },
      'submerchant-kl-003': {
        amount: 2200.0,
        chartData: [{
          date: formatShortDate(dates.d10),
          amount: 2200
        }],
        transactions: [{
          id: 'rsv_1c',
          customer: 'MK Reserve',
          amount: 2200
        }]
      },
      'submerchant-kl-004': {
        amount: 2050.0,
        chartData: [{
          date: formatShortDate(dates.d10),
          amount: 2050
        }],
        transactions: [{
          id: 'rsv_1d',
          customer: 'Galolhu Reserve',
          amount: 2050
        }]
      },
      'submerchant-kl-005': {
        amount: 2150.0,
        chartData: [{
          date: formatShortDate(dates.d10),
          amount: 2150
        }],
        transactions: [{
          id: 'rsv_1e',
          customer: 'MV Reserve',
          amount: 2150
        }]
      },
      'submerchant-kl-006': {
        amount: 2100.0,
        chartData: [{
          date: formatShortDate(dates.d10),
          amount: 2100
        }],
        transactions: [{
          id: 'rsv_1f',
          customer: 'Addu City Reserve',
          amount: 2100
        }]
      }
    },
    onHold: {
      'master-001': {
        amount: 5500.0,
        chartData: onHoldChartData,
        transactions: onHoldTransactions
      },
      'submerchant-kl-001': {
        amount: 1200.0,
        chartData: [{
          date: formatShortDate(dates.d5),
          amount: 1200
        }],
        transactions: [{
          id: 'hld_1a',
          customer: 'Majeedhee Magu Review',
          amount: 1200
        }]
      },
      'submerchant-kl-002': {
        amount: 950.0,
        chartData: [{
          date: formatShortDate(dates.d5),
          amount: 950
        }],
        transactions: [{
          id: 'hld_1b',
          customer: 'BB Review',
          amount: 950
        }]
      },
      'submerchant-kl-003': {
        amount: 800.0,
        chartData: [{
          date: formatShortDate(dates.d5),
          amount: 800
        }],
        transactions: [{
          id: 'hld_1c',
          customer: 'MK Review',
          amount: 800
        }]
      },
      'submerchant-kl-004': {
        amount: 1050.0,
        chartData: [{
          date: formatShortDate(dates.d5),
          amount: 1050
        }],
        transactions: [{
          id: 'hld_1d',
          customer: 'Galolhu Review',
          amount: 1050
        }]
      },
      'submerchant-kl-005': {
        amount: 750.0,
        chartData: [{
          date: formatShortDate(dates.d5),
          amount: 750
        }],
        transactions: [{
          id: 'hld_1e',
          customer: 'MV Review',
          amount: 750
        }]
      },
      'submerchant-kl-006': {
        amount: 750.0,
        chartData: [{
          date: formatShortDate(dates.d5),
          amount: 750
        }],
        transactions: [{
          id: 'hld_1f',
          customer: 'Addu City Review',
          amount: 750
        }]
      }
    }
  }), [dates]);
  // Derive chart + totals from the shared deterministic mock so that
  // BalanceSummary and BalanceActivity stay perfectly consistent.
  const getBalanceForCompany = (balanceType: 'incoming' | 'available' | 'reserved' | 'onHold') => {
    const companyType = selectedCompany.type === 'master' ? 'master' : 'submerchant';
    const chartData = getChartDataForCompany(balanceType, companyType, selectedCompany.id);
    const amount = getTotalForCompany(balanceType, companyType, selectedCompany.id);
    return {
      amount,
      chartData,
      transactions: []
    };
  };
  const incomingData = getBalanceForCompany('incoming');
  const availableData = (() => {
    const base = getBalanceForCompany('available');
    const companyType = selectedCompany.type === 'master' ? 'master' : 'submerchant';
    // For Available, the merchant cares about when funds will leave Available
    // and hit their bank account — i.e. the payout schedule — not when they
    // landed in Available. Surface the actual payout schedule here.
    const payoutChart = getPayoutScheduleForCompany(companyType, selectedCompany.id);
    return {
      ...base,
      chartData: payoutChart.length > 0 ? payoutChart : base.chartData
    };
  })();
  const reservedData = getBalanceForCompany('reserved');
  const onHoldData = getBalanceForCompany('onHold');
  const displayBalance = simulateNegative ? -4232.75 : availableData.amount;
  const isNegative = displayBalance < 0;
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const toggleDate = (section: string, date: string) => {
    setExpandedDates(prev => ({
      ...prev,
      [section]: prev[section] === date ? '' : date
    }));
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  };
  const lastUpdated = useMemo(() => randomSecondsAgo(), []);
  const balanceTypes = [{
    key: 'incoming',
    label: 'Incoming',
    description: 'Recent payments still being processed before they become available',
    amount: incomingData.amount,
    colorDot: 'bg-amber-400',
    colorPrimary: '#F59E0B',
    colorSecondary: '#FCD34D',
    colorBadgeBg: 'bg-amber-50',
    colorBadgeBorder: 'border-amber-200',
    colorBadgeText: 'text-amber-600',
    sectionTitle: 'BECOMING AVAILABLE',
    badgeText: 'Next',
    chartData: incomingData.chartData,
    transactions: incomingData.transactions,
    collapsedDates: []
  }, {
    key: 'available',
    label: 'Available',
    description: 'Funds you can use now for payouts, refunds, and account charges',
    amount: availableData.amount,
    negativeAmount: -4232.75,
    colorDot: 'bg-emerald-500',
    colorPrimary: '#10B981',
    colorSecondary: '#34D399',
    colorBadgeBg: 'bg-emerald-50',
    colorBadgeBorder: 'border-emerald-200',
    colorBadgeText: 'text-emerald-700',
    sectionTitle: 'UPCOMING PAYOUT SCHEDULE',
    badgeText: 'Next payout',
    chartData: availableData.chartData,
    transactions: availableData.transactions,
    collapsedDates: []
  }, {
    key: 'reserved',
    label: 'Reserved',
    description: '10% of payments held for 90 days per your reserve policy',
    amount: reservedData.amount,
    colorDot: 'bg-indigo-500',
    colorPrimary: '#6366F1',
    colorSecondary: '#A5B4FC',
    colorBadgeBg: 'bg-indigo-50',
    colorBadgeBorder: 'border-indigo-200',
    colorBadgeText: 'text-indigo-700',
    sectionTitle: 'UPCOMING RESERVE RELEASES',
    badgeText: 'Next release',
    infoBanner: <div className="flex items-start gap-3 p-4 mb-8 bg-indigo-50 border border-indigo-200 rounded-lg" data-id="element-2975">
          <InfoCircleIcon size={18} className="text-indigo-700 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-2976" />
          <p className="text-sm text-gray-700" data-id="element-2977">
            <span className="font-semibold text-gray-900" data-id="element-2978">
              {t('pages.balanceTypes.reserve_policy')}
            </span>{' '}
            {t('pages.balanceTypes.10_of_each_payment_is_held')}
          </p>
        </div>,
    chartData: reservedData.chartData,
    transactions: reservedData.transactions,
    collapsedDates: []
  }, {
    key: 'onHold',
    label: 'On hold',
    description: 'Funds temporarily unavailable while certain transactions or account activity are reviewed',
    amount: onHoldData.amount,
    colorDot: 'bg-red-400',
    colorPrimary: '#EF4444',
    colorSecondary: '#FCA5A5',
    colorBadgeBg: 'bg-red-50',
    colorBadgeBorder: 'border-red-200',
    colorBadgeText: 'text-red-600',
    sectionTitle: 'EXPECTED RESOLUTION',
    badgeText: 'Next review',
    chartData: onHoldData.chartData,
    transactions: onHoldData.transactions,
    collapsedDates: []
  }];
  const totalBalance = balanceTypes.reduce((sum, type) => sum + type.amount, 0);
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg" data-id="element-2979">
          <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2980">{label}</p>
          <p className="text-sm text-gray-700" data-id="element-2981">
            Available: {formatCurrency(payload[0].value)}
          </p>
        </div>;
    }
    return null;
  };
  return <div className="max-w-7xl mx-auto py-8 px-4" data-id="element-2982">
      {/* Simulate toggle — dev tool */}
      <div className="mb-4 flex items-center gap-3" data-id="element-2983">
        <button onClick={() => {
        setSimulateNegative(!simulateNegative);
        setShowMinBanner(true);
        setMinBalanceSaved(false);
      }} className="relative flex-shrink-0" data-id="element-2984">
          <div className={`w-9 h-5 rounded-full transition-colors ${simulateNegative ? 'bg-red-500' : 'bg-gray-300'}`} data-id="element-2985">
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${simulateNegative ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-2986" />
          </div>
        </button>
        <span className="text-xs text-gray-500 font-medium" data-id="element-2987">
          {t('pages.BalanceSummary.simulate_negative_balance')}
        </span>
      </div>

      {/* Negative balance warning banner */}
      {isNegative && showMinBanner && !minBalanceSaved && <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 relative" data-id="element-2988">
          <button onClick={() => setShowMinBanner(false)} aria-label="Dismiss notification" className="absolute top-3 right-3 text-amber-700/60 hover:text-amber-800 transition-colors" data-id="element-2989">
            <XIcon size={16} aria-hidden="true" data-id="element-2990" />
          </button>
          <div className="flex items-start gap-3" data-id="element-2991">
            <AlertTriangleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2992" />
            <div data-id="element-2993">
              <p className="text-sm font-semibold text-amber-900 mb-1" data-id="element-2994">
                {t('pages.BalanceSummary.set_a_minimum_to_avoid_negative')}
              </p>
              <p className="text-sm text-amber-800 leading-relaxed mb-3" data-id="element-2995">
                {t('pages.BalanceSummary.your_account_has_a_negative_balance')}
              </p>
              <button onClick={() => navigate('/settings/payouts')} className="px-4 py-2 text-sm font-medium text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors shadow-sm" data-id="element-2996">
                {t('pages.BalanceSummary.set_minimum_balance')}
              </button>
            </div>
          </div>
        </div>}

      {/* Minimum balance saved confirmation */}
      {isNegative && minBalanceSaved && <div className="mb-6 bg-green-50 border border-green-200 rounded-xl px-5 py-4" data-id="element-2997">
          <div className="flex items-center gap-3" data-id="element-2998">
            <ShieldIcon size={18} className="text-green-600 flex-shrink-0" data-id="element-2999" />
            <div data-id="element-3000">
              <p className="text-sm font-medium text-green-800" data-id="element-3001">
                {t('pages.BalanceSummary.minimum_balance_set_to_usd')}{' '}
                {Number(minBalanceAmount).toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
              </p>
              <p className="text-xs text-green-600 mt-0.5" data-id="element-3002">
                {t('pages.BalanceSummary.well_automatically_top_up_your_account')}
              </p>
            </div>
          </div>
        </div>}

      {/* Header */}
      <div className="mb-10" data-id="element-3003">
        <div className="flex items-center justify-between mb-2" data-id="element-3004">
          <div className="flex items-center gap-2" data-id="element-3005">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-3006">
              {t('pages.BalanceSummary.available_balance')}
            </h1>
            <div className="relative group cursor-pointer" data-id="element-3007">
              <InfoIcon size={20} className="text-gray-400 hover:text-gray-600" data-id="element-3008" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-3009">
                {t('pages.BalanceSummary.the_sum_of_your_available_payments')}
              </div>
            </div>
          </div>
          <button onClick={() => {
          const now = new Date();
          const reportDate = now.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });
          const timestamp = now.toISOString();
          const rows = [['Report date', 'Merchant account ID', 'Merchant account name', 'Currency', 'Balance type', 'Balance amount', 'Balance status', 'Description', 'As of timestamp', 'Report generated at'], [reportDate, 'MERCH-001', 'ABC Store', getTenant().currency.code, 'Incoming', '3250.00', 'Processing', 'Recent payments still being processed before they become available', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', getTenant().currency.code, 'Available', simulateNegative ? '-4232.75' : '61767.25', 'Active', 'Funds you can use now for payouts, refunds, and account charges', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', getTenant().currency.code, 'Reserved', '12450.00', 'Held', '10% of payments held for 90 days per your reserve policy', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', getTenant().currency.code, 'On hold', '5500.00', 'Under review', 'Funds temporarily unavailable while certain transactions or account activity are reviewed', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', getTenant().currency.code, 'Total balance', simulateNegative ? '16967.25' : '82967.25', 'Active', 'Total of all balance types', timestamp, timestamp]];
          const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
          const blob = new Blob([csv], {
            type: 'text/csv;charset=utf-8;'
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `balance-summary-${now.toISOString().split('T')[0]}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3010">
            <DownloadIcon size={16} data-id="element-3011" />
            {t('pages.BalanceActivity.export')}
          </button>
        </div>
        <div className={`text-4xl font-bold mb-2 flex items-center gap-2 ${isNegative ? 'text-red-600' : 'text-gray-900'}`} data-id="element-3012">
          {isNegative ? '−' : ''} {formatCurrency(Math.abs(displayBalance))}
          {isNegative && <AlertTriangleIcon size={24} className="text-red-500" data-id="element-3013" />}
        </div>
        <p className="text-sm text-gray-500" data-id="element-3014">Updated {lastUpdated}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8" data-id="element-3015">
        {/* Main Content - Balance Summary Table */}
        <div className="flex-1" data-id="element-3016">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3017">
            {t('pages.BalanceSummary.balance_summary')}
          </h2>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3018">
            {/* Table Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50/50" data-id="element-3019">
              <span className="text-sm font-medium text-gray-600" data-id="element-3020">
                {t('pages.BalanceActivity.balance_type')}
              </span>
              <span className="text-sm font-medium text-gray-600" data-id="element-3021">
                {t('pages.BalanceSummary.amount')}
              </span>
            </div>

            <div className="divide-y divide-gray-200" data-id="element-3022">
              {balanceTypes.map(type => {
              const isAvailable = type.key === 'available';
              const displayAmt = isAvailable && simulateNegative && type.negativeAmount !== undefined ? type.negativeAmount : type.amount;
              const isNegativeAmt = displayAmt < 0;
              const isOnHold = type.key === 'onHold';
              return <div key={type.key} data-id="element-3023">
                    {isOnHold ? <div className="w-full flex items-start justify-between px-6 py-4 text-left" data-id="element-3024">
                        <div className="flex items-start gap-3" data-id="element-3025">
                          <div className={`w-3.5 h-3.5 ${type.colorDot} rounded-sm mt-1 flex-shrink-0`} data-id="element-3026"></div>
                          <div data-id="element-3027">
                            <div className="flex items-center gap-2" data-id="element-3028">
                              <span className="text-sm font-semibold text-gray-900" data-id="element-3029">
                                {type.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5" data-id="element-3030">
                              {type.description}
                            </p>
                          </div>
                        </div>
                        <span className="text-sm font-medium mt-0.5 whitespace-nowrap flex-shrink-0 text-gray-900" data-id="element-3031">
                          {formatCurrency(displayAmt)}
                        </span>
                      </div> : <button onClick={() => toggleSection(type.key)} className="w-full flex items-start justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left" data-id="element-3032">
                        <div className="flex items-start gap-3" data-id="element-3033">
                          <div className={`w-3.5 h-3.5 ${type.colorDot} rounded-sm mt-1 flex-shrink-0`} data-id="element-3034"></div>
                          <div data-id="element-3035">
                            <div className="flex items-center gap-2" data-id="element-3036">
                              <span className="text-sm font-semibold text-gray-900" data-id="element-3037">
                                {type.label}
                              </span>
                              {expandedSection === type.key ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-3038" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-3039" />}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5" data-id="element-3040">
                              {type.description}
                            </p>
                          </div>
                        </div>
                        <span className={`text-sm font-medium mt-0.5 whitespace-nowrap flex-shrink-0 ${isNegativeAmt ? 'text-red-600' : 'text-gray-900'}`} data-id="element-3041">
                          {isNegativeAmt ? '− ' : ''}
                          {formatCurrency(Math.abs(displayAmt))}
                        </span>
                      </button>}

                    {!isOnHold && expandedSection === type.key && <div className="px-6 py-6 bg-gray-50/50 border-t border-gray-100" data-id="element-3042">
                        {isAvailable && simulateNegative ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-3043">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-3044">
                              <AlertTriangleIcon size={20} className="text-gray-400" data-id="element-3045" />
                            </div>
                            <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-3046">
                              {t('pages.BalanceSummary.no_upcoming_payouts')}
                            </p>
                            <p className="text-xs text-gray-500 max-w-xs" data-id="element-3047">
                              {t('pages.BalanceSummary.your_account_has_a_negative_balance_2')}
                            </p>
                          </div> : <>
                            {type.infoBanner}
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6" data-id="element-3048">
                              {type.sectionTitle}
                            </h4>

                            {/* Chart */}
                            <div className="h-48 mb-8" role="img" aria-label={`${type.label} balance — ${type.sectionTitle.toLowerCase()}. ${type.chartData.map((d: any) => `${d.date}: ${formatCurrency(d.amount)}`).join('; ')}.`} data-id="element-3049">
                              <ResponsiveContainer width="100%" height="100%" data-id="element-3050">
                                <BarChart data={type.chartData} margin={{
                          top: 0,
                          right: 0,
                          left: -20,
                          bottom: 0
                        }} data-id="element-3051">
                                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                            fontSize: 12,
                            fill: '#6B7280'
                          }} dy={10} data-id="element-3052" />

                                  <YAxis axisLine={false} tickLine={false} tick={{
                            fontSize: 12,
                            fill: '#6B7280'
                          }} tickFormatter={val => val >= 1000 ? `${val / 1000}K` : val} data-id="element-3053" />

                                  <Tooltip content={<CustomTooltip data-id="element-3055" />} cursor={{
                            fill: 'transparent'
                          }} data-id="element-3054" />

                                  <Bar dataKey="amount" radius={[4, 4, 0, 0]} maxBarSize={50} data-id="element-3056">
                                    {type.chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={index === 0 ? type.colorPrimary : type.colorSecondary} data-id="element-3057" />)}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Date List — one row per date, navigates to filtered Activity */}
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3058">
                              {type.chartData.map((entry: any, idx: number) => {
                        const txnCount = entry.count ?? 0;
                        return <button key={`${entry.date}-${idx}`} onClick={() => navigate(`/balance-activity?type=${type.key}&date=${encodeURIComponent(entry.date)}`)} className="w-full flex items-center justify-between px-4 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors text-left" data-id="element-3059">
                                    <div className="flex items-center gap-3 min-w-0" data-id="element-3060">
                                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap" data-id="element-3061">
                                        {entry.date} {new Date().getFullYear()}
                                      </span>
                                      {idx === 0 && <span className={`px-2 py-0.5 text-xs font-medium ${type.colorBadgeText} ${type.colorBadgeBg} border ${type.colorBadgeBorder} rounded-full whitespace-nowrap`} data-id="element-3062">
                                          {type.badgeText}
                                        </span>}
                                      <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-3063">
                                        {txnCount.toLocaleString()}{' '}
                                        {t('pages.BalanceSummary.txns')}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3064">
                                      <span className={`text-sm font-semibold ${idx === 0 ? type.colorBadgeText : 'text-gray-900'}`} data-id="element-3065">
                                        {formatCurrency(entry.amount)}
                                      </span>
                                      <ChevronRightIcon size={16} className="text-gray-400" data-id="element-3066" />
                                    </div>
                                  </button>;
                      })}
                            </div>
                          </>}
                      </div>}
                  </div>;
            })}

              {/* Total */}
              <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50" data-id="element-3067">
                <span className="text-sm font-bold text-gray-900" data-id="element-3068">
                  {t('pages.BalanceSummary.total_balance')}
                </span>
                <span className="text-sm font-bold text-gray-900" data-id="element-3069">
                  {formatCurrency(totalBalance)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SET MINIMUM BALANCE MODAL ─── */}
      {showMinBalanceModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setShowMinBalanceModal(false);
    }} data-id="element-3070">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-3071">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-3072">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-3073">
                  {t('pages.BalanceSummary.set_minimum_balance')}
                </h3>
                <button onClick={() => setShowMinBalanceModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3074">
                  <XIcon size={18} aria-hidden="true" data-id="element-3075" />
                </button>
              </div>
              <div className="p-6 space-y-4" data-id="element-3076">
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3077">
                  {t('pages.BalanceSummary.when_your_balance_drops_below_this')}
                </p>
                <div data-id="element-3078">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3079">
                    {t('pages.BalanceSummary.minimum_balance_usd')}
                  </label>
                  <div className="relative" data-id="element-3080">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" data-id="element-3081">
                      {t('pages.BalanceActivity.usd')}
                    </span>
                    <input type="number" className="block w-full pl-12 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400" placeholder={t('pages.BalanceSummary.attrs.eg_5000')} value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} autoFocus data-id="element-3082" />
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-id="element-3083">
                  <div className="flex items-start gap-2" data-id="element-3084">
                    <AlertTriangleIcon size={14} className="text-amber-500 flex-shrink-0 mt-0.5" data-id="element-3085" />

                    <p className="text-xs text-amber-800 leading-relaxed" data-id="element-3086">
                      {t('pages.BalanceSummary.your_current_balance_is')}{' '}
                      <span className="font-semibold" data-id="element-3087">
                        {formatCurrency(Math.abs(displayBalance))}
                      </span>{' '}
                      {t('pages.BalanceSummary.in_deficit_setting_a_minimum_will')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-3088">
                <button onClick={() => setShowMinBalanceModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-3089">
                  {t('pages.BalanceSummary.cancel')}
                </button>
                <button onClick={() => {
            if (minBalanceAmount && Number(minBalanceAmount) > 0) {
              setMinBalanceSaved(true);
              setShowMinBalanceModal(false);
              setShowMinBanner(false);
            }
          }} disabled={!minBalanceAmount || Number(minBalanceAmount) <= 0} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${minBalanceAmount && Number(minBalanceAmount) > 0 ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-3090">
                  {t('pages.BalanceSummary.save_minimum')}
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}